import { FC, useState } from 'react';
import ScrollAnimation from 'react-animate-on-scroll';
import styled from 'styled-components';

import ClickableButton from '@/components/Buttons/ClickableButton';
import Container from '@/components/Container/Container';
import Faq from '@/components/Faq/Faq';
import Footer from '@/components/Footer/Footer';
import FooterBanner from '@/components/FooterBanner/FooterBanner';
import HubspotForm from '@/components/Modal/HubspotForm';
import HubspotFormModal from '@/components/Modal/HubspotFormModal';
import Navigation from '@/components/Navigation/Navigation';
import Seo from '@/components/Seo/Seo';
import {
	HUBSPOT_FORM_IDS,
	HUBSPOT_FORM_TITLES,
	LANGUAGES,
} from '@/lib/constants';
import { fetchAPI, getImage } from '@/lib/fetchAPI';
import { parseHTML } from '@/lib/parse';
import { translate } from '@/lib/translate';
import ChatbotPricing from '@/modules/chatbots/ChatbotPricing';
import ChatbotScrollSlider from '@/modules/chatbots/ChatbotScrollSlider';
import GetButton from '@/modules/chatbots/GetButton';
import StartWithViberChatbot from '@/modules/chatbots/StartWithViberChatbot';
import StayInTouch from '@/modules/chatbots/StayInTouch';

const Chatbots: FC<any> = ({ data, lang }) => {
	const [currentFormId, setCurrentFormId] = useState<string | null>(null);

	const openModal = (formId) => setCurrentFormId(formId);

	const closeModal = () => setCurrentFormId(null);

	return (
		<>
			<OverflowContainer>
				<Seo
					url="/chatbots"
					title={translate(lang, {
						en: 'Viber Chatbots',
						ru: 'Viber Чат-боты',
						ua: 'Viber Чат-боти',
					})}
					description={translate(lang, {
						en: 'Automate communication and offer 24/7 customer support with Viber Chatbots.',
						ru: 'Автоматизируйте общение и предлагайте круглосуточную поддержку клиентам с чат-ботами Viber.',
						ua: 'Автоматизуйте спілкування та надайте цілодобову підтримку клієнтам з чат-ботами Viber.',
					})}
				/>

				<Navigation lang={lang} />
			</OverflowContainer>

			<OverflowContainer>
				<Heading>
					<MobRightLine
						alt="RightVectorMob"
						src="/images/chatbots/VectorRightMobile.svg"
					/>

					<MobLeftLine
						alt="LeftVectorMob"
						src="/images/chatbots/VectorLeftMobile.svg"
					/>

					<MobLeftGrad
						loading="lazy"
						alt="Gradient"
						src="/images/chatbots/MobLeftGrad.png"
					/>

					<MobRightGrad
						loading="lazy"
						alt="Gradient"
						src="/images/chatbots/MobRightGrad.png"
					/>

					<RightLine src="/images/chatbots/VectorRight.svg" alt="RightVector" />

					<LeftLine src="/images/chatbots/VectorLeft.svg" alt="LeftVector" />

					<LeftGrad
						loading="lazy"
						alt="Gradient"
						src="/images/chatbots/LeftGrad.png"
					/>

					<RightGrad
						loading="lazy"
						alt="Gradient"
						src="/images/chatbots/RightGrad.png"
					/>

					<ScrollAnimation animateOnce animateIn="fadeInUp">
						<TitleWrapper>
							<BotIconWrapper>
								<img
									alt={data?.hero?.titleIcon?.name}
									src={getImage(data?.hero?.titleIcon)}
								/>
							</BotIconWrapper>

							<Title>{parseHTML(data?.hero?.title)}</Title>
						</TitleWrapper>

						<DescWrapper>
							<SmileIconWrapper>
								<img
									src={getImage(data?.hero?.icon)}
									alt={data?.hero?.icon?.name}
								/>
							</SmileIconWrapper>

							<Description>{parseHTML(data?.hero?.description)}</Description>

							<PostDescription>
								{parseHTML(data?.hero?.descriptionPost)}
							</PostDescription>
						</DescWrapper>
					</ScrollAnimation>
				</Heading>

				<HeroSecondaryInfo>
					<Circle>{data?.kpiBlock?.textInCircle}</Circle>

					{data?.kpiBlock?.items?.map((tile, index) => (
						<InfoTile key={`cb_kpi_${index}`}>
							<ScrollAnimation
								animateOnce
								offset={0}
								animateIn="fadeInUp"
								delay={(index + 1) * 200}
							>
								<InfoTileIconWrapper>
									<img src={getImage(tile.image)} alt={tile.image?.name} />
								</InfoTileIconWrapper>

								<InfoTileText>{parseHTML(tile?.description)}</InfoTileText>

								<InfoTilePercent>{tile?.number}</InfoTilePercent>
							</ScrollAnimation>
						</InfoTile>
					))}
				</HeroSecondaryInfo>

				<StayInTouch data={data?.stayInTouch} />
			</OverflowContainer>

			<ScrollAnimation animateOnce animateIn="fadeInUp">
				<div>
					<ChatbotScrollSlider data={data?.slider} />
				</div>
			</ScrollAnimation>

			<Wrapper>
				<Container>
					<ChatbotPricing data={data?.pricingCB} />

					{data?.faqs?.length > 0 && (
						<ScrollAnimation animateOnce animateIn="fadeInUp" offset={0}>
							<Faq data={data?.faqs} />
						</ScrollAnimation>
					)}

					{data?.rateCta ? (
						<ButtonContainerainer>
							<ScrollAnimation animateOnce animateIn="fadeInUp" offset={0}>
								<ClickableButton
									text={data?.rateCta?.text}
									href={data?.rateCta?.link}
								/>
							</ScrollAnimation>
						</ButtonContainerainer>
					) : null}

					<StartWithViberChatbot
						data={data?.startWithCB}
						handleClick={() => openModal(HUBSPOT_FORM_IDS.MAIN)}
					/>

					<ScrollAnimation animateOnce offset={0} animateIn="fadeInUp">
						<GetStartedContainer>
							<GetLeft>
								<GetTitle>{parseHTML(data?.ctaBlock?.title)}</GetTitle>

								<GetDescription>
									{parseHTML(data?.ctaBlock?.description)}
								</GetDescription>
							</GetLeft>

							<GetRight>
								<ScrollAnimation
									animateOnce
									offset={0}
									delay={200}
									animateIn="fadeInUp"
								>
									<GetButton
										width="wide"
										text={data?.ctaBlock?.cta?.text}
										onClick={() => openModal(HUBSPOT_FORM_IDS.MAIN)}
									/>
								</ScrollAnimation>
							</GetRight>
						</GetStartedContainer>
					</ScrollAnimation>
				</Container>
			</Wrapper>

			<FooterBanner
				chatbot
				responsive
				viberForBusiness
				title={data?.bottomBanner?.title}
				cta={{
					text: data?.bottomBanner?.cta?.text,
					link: data?.bottomBanner?.cta?.link,
				}}
				image={{
					alt: data?.bottomBanner?.image?.name,
					link: data?.bottomBanner?.image?.url,
				}}
			/>

			<Footer lang={lang} />

			{currentFormId ? (
				<HubspotFormModal handleClose={closeModal}>
					<h2>{HUBSPOT_FORM_TITLES.MAIN}</h2>

					<HubspotForm formId={currentFormId} handleClose={closeModal} />
				</HubspotFormModal>
			) : null}
		</>
	);
};

const OverflowContainer = styled.div`
	overflow: hidden;
	max-width: 100%;
`;

const TitleWrapper = styled.div`
	display: flex;
	justify-content: center;

	@media (max-width: 500px) {
		flex-direction: column;
		align-items: center;
		margin-top: -60px;
	}
`;

const Title = styled.h1`
	font-family: var(--font-sans);

	font-weight: var(--font-bold);
	font-size: 65px;
	line-height: 60px;
	text-align: center;
	color: var(--color-purple);

	@media (max-width: 600px) {
		font-size: 40px;
		line-height: 40px;
	}
`;

const BotIconWrapper = styled.div`
	width: 52px;
	height: 52px;
	margin: 10px 20px 0 0;
	z-index: -100;
	img {
		max-width: 100%;
		max-height: 100%;
	}

	@media (max-width: 500px) {
		margin: 0 0 0 12px;
	}
`;

const DescWrapper = styled.div`
	display: flex;
	justify-content: center;
	flex-direction: column;
	align-items: center;
	margin-top: 80px;

	@media (max-width: 500px) {
		margin-top: 60px;
	}
`;

const SmileIconWrapper = styled.div`
	width: 100px;
	height: 100px;
	z-index: -100;
	img {
		max-width: 100%;
		max-height: 100%;
	}

	@media (max-width: 850px) {
		margin-bottom: 15px;
	}

	@media (max-width: 500px) {
		width: 80px;
		height: 80px;
		top: 4.8%;
		left: 35%;
	}
`;

const Description = styled.h4`
	font-family: var(--font-sans);

	font-size: 28px;
	line-height: 35px;
	text-align: center;
	color: var(--color-black);
	max-width: 50%;
	margin-top: 30px;

	@media (max-width: 500px) {
		font-size: 22px;
		line-height: 30px;
		max-width: 100%;
	}
`;

const PostDescription = styled.div`
	font-family: var(--font-sans);

	font-size: 18px;
	line-height: 25px;
	text-align: center;
	color: var(--color-purple-50);
	width: 18%;
	margin: 20px auto 80px;

	@media (max-width: 1900px) {
		width: 19%;
	}

	@media (max-width: 1800px) {
		width: 20%;
	}

	@media (max-width: 1700px) {
		width: 22%;
	}

	@media (max-width: 1600px) {
		width: 24%;
	}

	@media (max-width: 1500px) {
		width: 25%;
	}

	@media (max-width: 1400px) {
		width: 27%;
	}

	@media (max-width: 1300px) {
		width: 29%;
	}

	@media (max-width: 1200px) {
		width: 31%;
	}

	@media (max-width: 1100px) {
		width: 34%;
	}

	@media (max-width: 1000px) {
		width: 38%;
	}

	@media (max-width: 900px) {
		width: 43%;
	}

	@media (max-width: 800px) {
		width: 49%;
	}

	@media (max-width: 750px) {
		margin: 20px auto 0;
	}

	@media (max-width: 700px) {
		width: 58%;
		margin: 20px 15px 0;
	}

	@media (max-width: 600px) {
		width: 68%;
	}

	@media (max-width: 500px) {
		width: 85%;
	}

	@media (max-width: 400px) {
		width: 95%;
	}
`;

const MobRightLine = styled.img`
	display: none;
	z-index: -100;
	overflow: hidden;

	@media (max-width: 500px) {
		display: block;
		position: absolute;

		left: 62%;
		width: 38%;
		top: 52.4%;
	}
`;

const MobLeftLine = styled.img`
	display: none;
	z-index: -100;
	overflow: hidden;

	@media (max-width: 500px) {
		display: block;
		position: absolute;
		top: 30%;
		right: 62%;
		width: 39%;
	}
`;

const MobLeftGrad = styled.img`
	display: none;
	z-index: -100;
	overflow: hidden;

	@media (max-width: 500px) {
		display: block;
		position: absolute;
		left: 0;
		top: 0;
	}
`;

const MobRightGrad = styled.img`
	display: none;
	z-index: -100;
	overflow: hidden;

	@media (max-width: 500px) {
		display: block;
		position: absolute;
		right: 0;
		top: 0;
	}
`;

const RightLine = styled.img`
	position: absolute;
	top: 24.5%;
	z-index: -100;
	right: 0%;
	width: 45%;
	overflow: hidden;

	@media (max-width: 1700px) {
		top: 28.5%;
	}

	@media (max-width: 1400px) {
		top: 33.5%;
		width: 43%;
	}

	@media (max-width: 1200px) {
		top: 37.5%;
	}

	@media (max-width: 1000px) {
		top: 39.5%;
	}

	@media (max-width: 850px) {
		left: 57%;
		width: 43%;
	}

	@media (max-width: 750px) {
		top: 49.5%;
		left: 61%;
		width: 39%;
	}

	@media (max-width: 500px) {
		display: none;
	}
`;

const LeftLine = styled.img`
	position: absolute;
	top: 0;
	z-index: -100;
	right: 58%;
	width: 42%;
	overflow: hidden;

	@media (max-width: 1700px) {
		top: 5%;
		right: 59%;
		width: 42%;
	}

	@media (max-width: 1400px) {
		top: 13%;
		right: 61%;
		width: 39%;
	}

	@media (max-width: 1200px) {
		top: 14.8%;
		right: 63%;
	}

	@media (max-width: 1000px) {
		top: 18.8%;
		right: 65%;
		width: 40%;
	}

	@media (max-width: 850px) {
		top: 22.8%;
		right: 68%;
		width: 36%;
	}

	@media (max-width: 750px) {
		top: 28.8%;
		right: 71%;
		width: 34%;
	}

	@media (max-width: 500px) {
		display: none;
	}
`;

const LeftGrad = styled.img`
	position: absolute;
	z-index: -100;
	left: 0;
	top: 0;
	overflow: hidden;

	@media (max-width: 500px) {
		display: none;
	}
`;

const RightGrad = styled.img`
	position: absolute;
	right: 0;
	top: 0;
	z-index: -100;
	overflow: hidden;

	@media (max-width: 500px) {
		display: none;
	}
`;

const Wrapper = styled.div`
	overflow: hidden;
	max-width: 100%;
	position: relative;
`;

const Heading = styled.div`
	position: relative;
	padding-top: 155px;
`;

const HeroSecondaryInfo = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	height: 250px;
	width: 960px;
	border-radius: 50px;
	background-color: #f8f7fb;
	margin: 0 auto;
	position: relative;

	@media (max-width: 1000px) {
		width: 800px;
	}

	@media (max-width: 800px) {
		width: 90%;
	}

	@media (max-width: 750px) {
		margin-top: 100px;
		flex-direction: column;
		width: 90%;
		height: fit-content;
	}
`;

const Circle = styled.div`
	position: absolute;
	display: flex;
	justify-content: center;
	align-items: center;
	height: 120px;
	width: 120px;
	text-align: center;
	left: 0;
	right: 0;
	margin: 0 auto;
	font-family: var(--font-sans);
	font-weight: var(--font-bold);
	font-size: 14px;
	line-height: 15px;
	text-transform: uppercase;
	border: 1px solid var(--color-white-30);
	background-color: var(--color-white);
	box-shadow: 0px 20px 30px rgba(99, 90, 168, 0.08);
	border-radius: 200px;

	@media (max-width: 750px) {
		top: -60px;
		margin: auto;
	}
`;

const InfoTilePercent = styled.div`
	font-family: var(--font-sans);

	font-size: 28px;
	line-height: 35px;
	margin-top: 12px;
	color: var(--color-black);
	text-align: center;

	@media (max-width: 850px) {
		font-size: 22px;
		line-height: 30px;
		margin-bottom: 5px;
	}
`;

const InfoTileText = styled.div`
	font-family: var(--font-sans);

	font-size: 16px;
	line-height: 24px;
	color: var(--color-purple-50);
	text-align: center;
	margin: 0 auto;
	width: 285px;
	padding: 0px 20px;

	@media (max-width: 850px) {
		color: var(--color-black);
	}
`;

const InfoTile = styled.div`
	width: 44.43%;

	border-left: none;

	@media (max-width: 1000px) {
		margin: 0 25px;
	}

	@media (max-width: 850px) {
		width: 254px;
		border: none;
		padding: 0;
	}

	@media (max-width: 750px) {
		margin: 86px auto 32px;
		&:last-child {
			margin-bottom: 32px;
			margin-top: 0;
		}
	}

	@media (max-width: 500px) {
		margin: 86px auto 32px;
		&:last-child {
			margin-bottom: 20px;
			margin-top: 0;
		}
	}
`;

const InfoTileIconWrapper = styled.div`
	width: 60px;
	height: 60px;
	margin: 0 auto 30px;

	img {
		max-width: 100%;
		max-height: 100%;
	}

	@media (max-width: 850px) {
		margin-bottom: 15px;
	}
`;

const GetStartedContainer = styled.div`
	display: flex;
	width: 960px;
	height: 240px;
	max-width: 100%;
	margin: 0 auto;
	background-color: var(--color-white-30);
	mix-blend-mode: normal;
	border-radius: 60px;
	padding: 60px 60px;
	justify-content: space-between;
	align-items: flex-end;
	margin-top: 120px;
	margin-bottom: 160px;

	@media (max-width: 600px) {
		width: 345px;
		height: 310px;
		padding: 40px 24px 40px 30px;
		flex-direction: column;
		align-items: flex-start;
		margin-top: 20px;
		margin-bottom: 50px;
		border-radius: 30px;
	}
`;

const GetLeft = styled.div`
	width: 425px;

	@media (max-width: 765px) {
		width: 340px;
	}

	@media (max-width: 675px) {
		width: 270px;
	}

	@media (max-width: 600px) {
		width: 100%;
		margin-bottom: 30px;
	}
`;

const GetRight = styled.div`
	display: flex;
	justify-content: flex-end;

	@media (max-width: 600px) {
		width: 100%;
	}

	div:first-of-type {
		width: 100%;
	}
`;

const GetTitle = styled.h3`
	font-family: var(--font-sans);

	font-size: 28px;
	line-height: 35px;
	color: var(--color-black);
	margin-bottom: 20px;

	@media (max-width: 600px) {
		font-size: 22px;
		line-height: 30px;
	}
`;

const GetDescription = styled.div`
	font-family: var(--font-sans);

	font-size: 18px;
	line-height: 25px;
	color: var(--color-black);

	@media (max-width: 600px) {
		font-size: 16px;
	}
`;

const ButtonContainerainer = styled.div`
	display: flex;
	justify-content: center;

	@media (max-width: 500px) {
		.fadeInUp {
			width: 100%;
		}
	}
`;

export async function getStaticProps({ params }) {
	const { lang } = params;
	const page = await fetchAPI('/chatbot', {
		lang,
		populate: [
			'hero',
			'hero.icon',
			'hero.titleIcon',
			'kpiBlock',
			'kpiBlock.items',
			'kpiBlock.items.image',
			'stayInTouch',
			'stayInTouch.text',
			'stayInTouch.infoRow',
			'stayInTouch.infoRow.icon',
			'slider',
			'slider.cta',
			'slider.contentBlock',
			'slider.contentBlock.icon',
			'slider.contentBlock.iconActive',
			'slider.buttonImage',
			'commercialModel',
			'commercialModel.textBlock',
			'commercialModel.imageLeft',
			'commercialModel.mobileImage',
			'startWithCB',
			'startWithCB.cta',
			'startWithCB.steps',
			'ctaBlock',
			'ctaBlock.cta',
			'bottomBanner',
			'bottomBanner.cta',
			'bottomBanner.image',
			'pricingCB',
			'pricingCB.pricingCBBlock',
			'pricingCB.pricingCBBlock.icon',
			'faqs',
			'rateCta',
		],
	});

	return {
		props: { data: page?.data, lang },
	};
}

export async function getStaticPaths() {
	const paths = LANGUAGES.map(({ code }) => ({ params: { lang: code } }));

	return { paths, fallback: false };
}

export default Chatbots;
